/* rl-carousel — Swiper on the theme's horizontal rails, on phones.

   A rail scrolled natively (overflow-x + scroll-snap) is a scroller nested in
   the page: on iOS, a finger that lands on it to scroll the page down falls
   into it first, and the page scroll hesitates, mostly when the direction
   changes quickly (2026-10-11, product gallery; Chrome shows nothing). Swiper
   moves the rail with a transform and leaves the vertical pan to the page
   (touch-action: pan-y), as on the product gallery since then.

   Markup:
     [data-rl-carousel]           the frame (clips; Swiper's container)
       [data-rl-carousel-track]   the rail (Swiper's wrapper); its element
                                  children are the slides
   Options on the frame:
     data-rl-carousel-autoplay="5000"   ms per slide; rewinds after the last one.
                                        Stopped for good by a real swipe (a page
                                        scroll starting on the rail does not),
                                        paused off screen, in a hidden tab, under
                                        the mouse (devices with one) and while a
                                        keyboard focus is inside; after a pause
                                        of a second or more, the next slide
                                        comes a second after it ends. Never
                                        started under reduced motion.
     data-rl-carousel-autoplay-speed="1200"  ms of an autoplay move (a swipe
                                        keeps Swiper's quick 300 ms).
     data-rl-carousel-media="(…)"       where Swiper runs: phones by default,
                                        "all" for every width.

   Events on the frame: rl-carousel:change { index, initial }, once Swiper is up
   (initial: true) and on every slide change; rl-carousel:destroy when it leaves (back to desktop).
   The instance is frame.rlCarousel ({ swiper, slideTo(i) }) while it runs.

   Loaded by snippets/rl-carousel-script.liquid (data-swiper-src, data-css-href);
   Swiper is fetched once per page, or taken from the product page's tag. */
(() => {
  if (window.RlCarousel) return;

  const script = document.currentScript;
  const SWIPER_SRC = script?.dataset.swiperSrc;
  const CSS_HREF = script?.dataset.cssHref;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  // A touch screen fires mouseenter on tap and no mouseleave until another tap
  // elsewhere: hovering only pauses where there is a real mouse.
  const mouse = window.matchMedia('(hover: hover) and (pointer: fine)');
  const RESUME_AFTER_PAUSE_MS = 1000;

  let swiperReady = null;
  function loadSwiper() {
    if (typeof window.Swiper === 'function') return Promise.resolve();
    if (swiperReady) return swiperReady;
    swiperReady = new Promise((resolve, reject) => {
      // The product page loads Swiper itself (main-product.liquid): wait for it.
      const existing = [...document.scripts].find((tag) => /\/swiper-[\d.]+\.min\.js/.test(tag.src));
      const tag = existing || document.createElement('script');
      tag.addEventListener('load', () => resolve(), { once: true });
      tag.addEventListener('error', reject, { once: true });
      if (!existing) {
        tag.src = SWIPER_SRC;
        tag.fetchPriority = 'low';
        document.head.append(tag);
      } else if (typeof window.Swiper === 'function') {
        resolve();
      }
    });
    return swiperReady;
  }

  // Swiper measures the slides when it starts: its CSS must be in first.
  let cssReady = null;
  function loadCss() {
    if (!CSS_HREF) return Promise.resolve();
    cssReady ||= new Promise((resolve) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = CSS_HREF;
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', resolve, { once: true });
      document.head.append(link);
    });
    return cssReady;
  }

  // The slide a CSS animation (the hero's autoplay keyframes) is showing, so
  // Swiper starts where the visitor's eye already is.
  function shownIndex(track, slides) {
    const transform = getComputedStyle(track).transform;
    const width = slides[0]?.getBoundingClientRect().width || 1;
    if (!transform || transform === 'none') return 0;
    const shift = -new DOMMatrixReadOnly(transform).m41;
    return Math.max(0, Math.min(slides.length - 1, Math.round(shift / width)));
  }

  class Carousel {
    constructor(frame) {
      this.frame = frame;
      this.track = frame.querySelector('[data-rl-carousel-track]');
      const media = frame.dataset.rlCarouselMedia;
      this.media = window.matchMedia(!media ? '(max-width: 749px)' : media === 'all' ? 'all' : media);
      this.delay = Number(frame.dataset.rlCarouselAutoplay) || 0;
      this.autoplaySpeed = Number(frame.dataset.rlCarouselAutoplaySpeed) || undefined;
      this.timer = null;
      this.stopped = false;
      this.visible = true;
      this.hovered = false;
      this.focused = false;
      this.pausedSince = 0;
      this.resumeSoon = false;
      if (this.delay > 0) this.watchPause();
      this.onMedia = () => (this.media.matches ? this.start() : this.stop());
      this.media.addEventListener('change', this.onMedia);
      this.onVisibility = () => this.schedule();
      document.addEventListener('visibilitychange', this.onVisibility);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          this.schedule();
        });
        this.observer.observe(frame);
      }
      this.onMedia();
    }

    slides() {
      return [...this.track.children].filter((child) => child.nodeType === 1);
    }

    // Autoplay pauses under the mouse and while a keyboard focus is inside.
    watchPause() {
      const set = (key, on) => {
        const was = this.hovered || this.focused;
        this[key] = on;
        const now = this.hovered || this.focused;
        if (now && !was) this.pausedSince = performance.now();
        if (!now && was) this.resumeSoon = performance.now() - this.pausedSince >= RESUME_AFTER_PAUSE_MS;
        this.schedule();
      };
      this.frame.addEventListener('mouseenter', () => { if (mouse.matches) set('hovered', true); });
      this.frame.addEventListener('mouseleave', () => { if (this.hovered) set('hovered', false); });
      this.frame.addEventListener('focusin', (event) => {
        if (event.target.matches?.(':focus-visible')) set('focused', true);
      });
      this.frame.addEventListener('focusout', (event) => {
        if (this.focused && !this.frame.contains(event.relatedTarget)) set('focused', false);
      });
    }

    async start() {
      if (this.swiper || this.starting || !this.track) return;
      this.starting = true;
      try {
        await Promise.all([loadSwiper(), loadCss()]);
      } catch {
        return; // the rail stays as the theme draws it
      } finally {
        this.starting = false;
      }
      if (this.swiper || !this.media.matches) return;
      const slides = this.slides();
      if (slides.length < 2) return;
      const initialSlide = shownIndex(this.track, slides);
      // Swiper places the slides itself and ignores a CSS gap: the rail's gap
      // becomes its spaceBetween, and the CSS one is set aside.
      const spaceBetween = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      // An inline style outranks the stylesheet: the keyframes stop, Swiper's
      // transform takes over (an animated transform would override it).
      this.track.style.animation = 'none';
      this.track.style.gap = '0';
      this.track.classList.add('rl-carousel__track');
      slides.forEach((slide) => slide.classList.add('rl-carousel__slide'));
      this.frame.classList.add('rl-carousel--on');
      this.swiper = new window.Swiper(this.frame, {
        wrapperClass: 'rl-carousel__track',
        slideClass: 'rl-carousel__slide',
        slidesPerView: 'auto',
        spaceBetween,
        initialSlide,
        rewind: this.delay > 0,
        // Same feel as the product gallery.
        speed: 300,
        threshold: 5,
        longSwipesRatio: 0.5,
        resistanceRatio: 0.85,
        on: {
          slideChange: (swiper) => {
            this.emit('change', { index: swiper.activeIndex });
            this.schedule();
          },
          // A drag along the rail; a page scroll that starts on it never fires this.
          sliderMove: () => {
            this.stopped = true;
            this.schedule();
          }
        }
      });
      this.frame.rlCarousel = { swiper: this.swiper, slideTo: (index) => this.swiper?.slideTo(index) };
      this.emit('change', { index: this.swiper.activeIndex, initial: true });
      this.schedule();
    }

    stop() {
      if (!this.swiper) return;
      clearTimeout(this.timer);
      this.swiper.destroy(true, true);
      this.swiper = null;
      delete this.frame.rlCarousel;
      this.track.style.animation = '';
      this.track.style.gap = '';
      this.track.classList.remove('rl-carousel__track');
      this.slides().forEach((slide) => slide.classList.remove('rl-carousel__slide'));
      this.frame.classList.remove('rl-carousel--on');
      this.emit('destroy');
    }

    // One timer for the next slide, re-armed on every change; none while the
    // rail is off screen, the tab hidden, paused, or once the visitor has swiped.
    schedule() {
      clearTimeout(this.timer);
      const run = this.swiper && this.delay > 0 && !this.stopped && !reducedMotion.matches
        && this.visible && document.visibilityState === 'visible' && !this.hovered && !this.focused;
      if (!run) return;
      const wait = this.resumeSoon ? Math.min(this.delay, RESUME_AFTER_PAUSE_MS) : this.delay;
      this.resumeSoon = false;
      this.timer = setTimeout(() => this.swiper?.slideNext(this.autoplaySpeed), wait);
    }

    emit(name, detail = {}) {
      this.frame.dispatchEvent(new CustomEvent(`rl-carousel:${name}`, { detail }));
    }
  }

  function mountAll(root = document) {
    root.querySelectorAll('[data-rl-carousel]').forEach((frame) => {
      if (!frame.rlCarouselMounted) {
        frame.rlCarouselMounted = true;
        new Carousel(frame);
      }
    });
  }

  window.RlCarousel = { mountAll };
  mountAll();
  // Theme editor: a section re-rendered in place brings a new frame.
  document.addEventListener('shopify:section:load', (event) => mountAll(event.target));
})();
